import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Trash2 } from 'lucide-react';

function CommentsPanel({ comments = [], currentUserUuid, onAddComment, onDeleteComment, onClose }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [comments.length]);

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      await onAddComment(trimmed);
      setText('');
    } catch (error) {
      console.error('Failed to send comment:', error);
    }
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="absolute inset-0 z-40 flex items-end" onClick={onClose}>
      <div
        className="w-full max-h-[60%] bg-gray-800 rounded-t-2xl flex flex-col animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h3 className="text-white font-semibold">Comments ({comments.length})</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-700 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Comments List */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
          {comments.length === 0 ? (
            <p className="text-gray-500 text-center py-8 text-sm">No comments yet. Be the first!</p>
          ) : (
            comments.map((comment) => (
              <div key={comment.uuid} className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-semibold text-gray-300">
                    {comment.userName ? comment.userName[0].toUpperCase() : '?'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-semibold text-white">{comment.userName}</span>
                    <span className="text-[10px] text-gray-500">{formatDate(comment.created)}</span>
                  </div>
                  <p className="text-sm text-gray-300 mt-0.5">{comment.text}</p>
                </div>
                {comment.userUuid === currentUserUuid && (
                  <button
                    onClick={() => onDeleteComment(comment.uuid)}
                    className="p-1 hover:bg-gray-700 rounded-full transition-colors self-start flex-shrink-0"
                  >
                    <Trash2 className="w-4 h-4 text-gray-500 hover:text-red-400" />
                  </button>
                )}
              </div>
            ))
          )}
        </div>

        {/* Input */}
        <div className="p-3 border-t border-gray-700 flex items-center gap-2">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add a comment..."
            className="flex-1 bg-gray-700 text-white text-sm px-4 py-2.5 rounded-full focus:outline-none focus:ring-1 focus:ring-purple-500 placeholder-gray-500"
          />
          <button
            onClick={handleSend}
            disabled={!text.trim() || sending}
            className="p-2 text-purple-400 hover:text-purple-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default CommentsPanel;
